/**
 * apify.ts — turning what the Apify LinkedIn Ad Library actor returns into competitor ads, and the spend rules around it.
 *
 * The actor is a third party's (`alexmorain/linkedin-ad-library-scraper`). Its output fields were read from its public page
 * on 2026-10-25, not from a live run, so every field is treated as optional and a row we cannot use is skipped, never guessed.
 * Two facts from that page shape everything here:
 *  - Impressions, run dates and targeting exist only for ads served in the EU (LinkedIn's DSA transparency block). For US and
 *    Canada they come back empty, and empty is stored as unknown — never as 0.
 *  - A Thought Leader ad can name a real person. We keep the ad text and link, not the person's name.
 * Pure and dependency-light so the CMS Worker and the tests agree.
 */
import { z } from "zod";
import type { AD_FORMATS } from "./ads.ts";

export const APIFY_ACTOR = "alexmorain~linkedin-ad-library-scraper";

export const APIFY_LIMITS = {
  /** Ads asked for in one fetch. Keeps one click cheap and inside the actor's 300-second synchronous limit. */
  defaultAds: 20,
  maxAds: 50,
  /** Spend ceiling per calendar month when the manager has not set one. */
  defaultMonthlyCapUsd: 5,
  maxMonthlyCapUsd: 200,
  /** Worst-case price per ad with details on, on Apify's free tier (the page lists $0.002; higher tiers pay less). */
  perAdUsd: 0.002,
  startUsd: 0.0001,
} as const;

/** What a fetch can cost at most. An estimate from the actor's listed price, not a bill. */
export function estimateApifyCostUsd(ads: number): number {
  return Math.round((ads * APIFY_LIMITS.perAdUsd + APIFY_LIMITS.startUsd) * 10_000) / 10_000;
}

/** How many ads still fit under the monthly cap. */
export function adsAffordable(capUsd: number, spentUsd: number): number {
  const left = capUsd - spentUsd - APIFY_LIMITS.startUsd;
  return left <= 0 ? 0 : Math.floor(left / APIFY_LIMITS.perAdUsd);
}

const optString = z.string().nullish();
/** One item as the actor documents it. Everything is optional; unknown extra fields are ignored. */
export const apifyAdSchema = z
  .object({
    ad_id: z.union([z.string(), z.number()]).nullish(),
    detail_url: optString,
    advertiser: optString,
    format: optString,
    headline: optString,
    body: optString,
    body_preview: optString,
    cta: optString,
    ran_from: optString,
    ran_to: optString,
    impressions_min: z.number().nullish(),
    impressions_max: z.number().nullish(),
    impressions_by_country: z.array(z.object({ country: z.string().nullish(), share: z.union([z.string(), z.number()]).nullish() })).nullish(),
    has_details: z.boolean().nullish(),
  })
  .passthrough();
export type ApifyAd = z.infer<typeof apifyAdSchema>;

type AdFormat = (typeof AD_FORMATS)[number];

/** "Single Image Ad" → single_image. Anything unrecognised is "other", never a guess. */
export function apifyFormat(label: string | null | undefined): AdFormat {
  const t = (label ?? "").toLowerCase();
  if (t.includes("thought leader")) return "thought_leader";
  if (t.includes("carousel")) return "carousel";
  if (t.includes("video")) return "video";
  if (t.includes("document")) return "document";
  if (t.includes("event")) return "event";
  if (t.includes("message") || t.includes("conversation")) return "message";
  if (t.includes("text")) return "text";
  if (t.includes("single image") || t.includes("image")) return "single_image";
  return "other";
}

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
/** LinkedIn's display date ("Apr 23, 2026") → "2026-04-23"; anything else → null. */
export function parseLinkedInDate(s: string | null | undefined): string | null {
  const m = /^([A-Za-z]{3})[a-z]*\.?\s+(\d{1,2}),\s*(\d{4})$/.exec((s ?? "").trim());
  if (!m) return null;
  const mi = MONTHS.indexOf(m[1]!.toLowerCase());
  const day = Number(m[2]);
  if (mi < 0 || day < 1 || day > 31) return null;
  return `${m[3]}-${String(mi + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

const COMPANY_SUFFIXES = new Set(["inc", "llc", "ltd", "corp", "corporation", "co", "company", "limited", "plc"]);
const nameTokens = (s: string) => s.toLowerCase().split(/[^\p{L}\p{N}]+/u).filter((t) => t && !COMPANY_SUFFIXES.has(t));

/**
 * The actor searches by name and can return other companies' ads. True when the item belongs to the competitor we asked for:
 * same name, or the same name plus one more word ("Paradox" and "Paradox AI"). Thought-leader ads and items with no advertiser name are kept.
 */
export function isSameAdvertiser(raw: unknown, wanted: string): boolean {
  const parsed = apifyAdSchema.safeParse(raw);
  if (!parsed.success) return true;
  // A thought-leader ad is posted by a person, so its advertiser is a person's name, not the company's.
  if (apifyFormat(parsed.data.format) === "thought_leader") return true;
  const got = (parsed.data.advertiser ?? "").trim();
  if (!got) return true;
  const a = nameTokens(got);
  const w = nameTokens(wanted);
  if (!w.length || !a.length) return true;
  const short = a.length <= w.length ? a : w;
  const long = a.length <= w.length ? w : a;
  return short.every((t, i) => long[i] === t) && long.length - short.length <= 1;
}

export interface MappedCompetitorAd {
  headline: string;
  fullText: string;
  cta: string | null;
  format: AdFormat;
  adUrl: string | null;
  firstSeen: string | null;
  lastSeen: string | null;
  impressionsLow: number | null;
  impressionsHigh: number | null;
  topCountries: string[];
  thoughtLeader: boolean;
  detailsCheckedOn: string | null;
}

const httpOnly = (u: string | null | undefined): string | null => (u && /^https?:\/\/\S+$/i.test(u.trim()) ? u.trim().slice(0, 1000) : null);

/**
 * One actor item → one competitor ad, or null when it can't be used (no ad text). `today` is "YYYY-MM-DD".
 * Dates and impressions are kept only when the actor gave them (EU ads); otherwise they stay unknown.
 */
export function mapApifyAd(raw: unknown, today: string): MappedCompetitorAd | null {
  const parsed = apifyAdSchema.safeParse(raw);
  if (!parsed.success) return null;
  const a = parsed.data;
  const fullText = (a.body ?? a.body_preview ?? "").trim().slice(0, 8000);
  if (!fullText) return null;
  const format = apifyFormat(a.format);
  const firstSeen = parseLinkedInDate(a.ran_from);
  const lastSeen = parseLinkedInDate(a.ran_to);
  let low = a.impressions_min ?? null;
  let high = a.impressions_max ?? null;
  if (low !== null && (!Number.isFinite(low) || low < 0)) low = null;
  if (high !== null && (!Number.isFinite(high) || high < 0)) high = null;
  if (low !== null && high !== null && high < low) high = null;
  const countries = (a.impressions_by_country ?? [])
    .map((c) => (c.country ?? "").trim().toUpperCase())
    .filter((c) => /^[A-Z]{2}$/.test(c));
  return {
    headline: (a.headline ?? "").trim().slice(0, 300),
    fullText,
    cta: a.cta?.trim().slice(0, 80) || null,
    format,
    adUrl: httpOnly(a.detail_url),
    firstSeen,
    lastSeen: firstSeen && lastSeen && lastSeen < firstSeen ? null : lastSeen,
    impressionsLow: low,
    impressionsHigh: high,
    topCountries: [...new Set(countries)].slice(0, 10),
    thoughtLeader: format === "thought_leader",
    detailsCheckedOn: a.has_details ? today : null,
  };
}

export const apifyFetchBodySchema = z.object({
  siteId: z.string().min(1).max(100),
  watchlistId: z.string().min(1).max(100),
  maxAds: z.number().int().min(1).max(APIFY_LIMITS.maxAds).default(APIFY_LIMITS.defaultAds),
});
export type ApifyFetchBody = z.infer<typeof apifyFetchBodySchema>;

export const apifyStatusSchema = z.object({
  configured: z.boolean(),
  capUsd: z.number(),
  spentUsd: z.number(),
  /** Ads that still fit under this month's cap. */
  adsLeft: z.number().int(),
});
export type ApifyStatus = z.infer<typeof apifyStatusSchema>;

export const apifyFetchResultSchema = z.object({
  fetched: z.number().int(),
  /** Ads the actor returned that belong to other companies; they are not saved. */
  otherCompanies: z.number().int(),
  saved: z.number().int(),
  alreadySaved: z.number().int(),
  skippedNoText: z.number().int(),
  /** Saved ads with no impressions or dates, because LinkedIn publishes those only for the EU. */
  withoutEuData: z.number().int(),
  estimatedUsd: z.number(),
  /** True when this empty check was the third in a row and the competitor was switched off. */
  switchedOff: z.boolean(),
  /** Weeks in a row with no ads, after this check. */
  emptyWeeks: z.number().int(),
});
export type ApifyFetchResult = z.infer<typeof apifyFetchResultSchema>;
